/**
 * CopilotPanel.page.tsx — Standalone route wrapper for the AI Co-pilot panel.
 *
 * Provides a full-page view of the co-pilot capabilities when accessed
 * via direct route /copilot (per Surface Atlas §3.4).
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { CopilotPanel } from '../components/copilot/CopilotPanel';
import { BRAND_NAME } from '../config/brand';

/**
 * Standalone Copilot Page
 *
 * Query params:
 * - reportId: Case context for the copilot
 * - role: Lens (accountant | war-room | analyst | attorney)
 */
export function CopilotPanelPage() {
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('reportId') || '';
  const role = (searchParams.get('role') as Parameters<typeof CopilotPanel>[0]['role']) || 'accountant';

  // Set document title
  React.useEffect(() => {
    document.title = `${BRAND_NAME} Copilot — What-If Analysis`;
  }, []);

  return (
    <div className="h-screen flex bg-[var(--color-bg-base)]">
      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 border-b border-[var(--color-border)] flex items-center px-4 bg-[var(--color-bg-surface)]">
          <h1 className="text-lg font-serif font-semibold text-[var(--color-text-primary)]">
            {BRAND_NAME} Copilot
          </h1>
          <span className="ml-2 text-sm text-[var(--color-text-muted)]">
            Exploratory Analysis
          </span>
        </header>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {!reportId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <h2 className="text-xl font-medium text-[var(--color-text-primary)] mb-2">
                  AI Co-pilot
                </h2>
                <p className="text-sm text-[var(--color-text-muted)] max-w-md mx-auto">
                  Open the Co-pilot from a case workspace to run what-if scenarios,
                  compare engine runs, and view prediction accuracy metrics.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 p-6 overflow-y-auto">
                <div className="max-w-2xl mx-auto">
                  <div className="p-4 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
                    <div className="text-sm text-[var(--color-text-muted)]">
                      Case: <span className="font-mono text-[var(--color-text-primary)]">{reportId}</span>
                    </div>
                    <div className="text-sm text-[var(--color-text-muted)] mt-1">
                      The Co-pilot panel is available on the right →
                    </div>
                  </div>
                </div>
              </div>

              <CopilotPanel
                reportId={reportId}
                role={role}
                defaultExpanded={true}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CopilotPanelPage;
